import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { hasAdminCapability, useAuth } from '@services/auth';
import { getPublicPlans } from '@data/publicPlans';
import { projects, getProjectDisplayName } from '@data/projects';
import { tasks } from '@data/tasks';
import { plants } from '@data/plants';
import { pageSearchIndex } from '@data/searchIndex';
import NotificationDropdown from '@components/NotificationDropdown';
import { apiGet } from '@services/api';
import { getPlans, type Plan } from '@services/plans';
import { getImageUrl } from '@utils/imageUrl';
import { normalizeStoredImageRef } from '@utils/profilePicture';
import {
	getMyCompanyContexts,
	setMyActiveCompany,
	getStoredCompanyScopeMode,
	setStoredCompanyScopeMode,
	notifyCompanyWorkspaceChanged,
	type Company
} from '@services/companies';
import { useI18n, type Language } from '../i18n';

type TopbarProps = {
	onToggleSidebar?: () => void;
	isSidebarOpen?: boolean;
	/** When true, sidebar uses off-canvas drawer; menu button shows list/close icons. */
	isMobileLayout?: boolean;
};

type SearchResult = {
	type: 'plan' | 'project' | 'task' | 'plant' | 'page' | 'section';
	id: string;
	title: string;
	subtitle?: string;
	url: string;
	icon: string;
	sectionId?: string; // For highlighting sections
	tooltip?: string; // e.g. contact email on hover
};

export default function Topbar({
	onToggleSidebar,
	isSidebarOpen,
	isMobileLayout: isMobileLayoutProp
}: TopbarProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout, refreshUser } = useAuth();
	const { language, setLanguage, t } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [showResults, setShowResults] = useState(false);
	const searchRef = useRef<HTMLDivElement>(null);
	const mobileSearchRef = useRef<HTMLDivElement>(null);
	const desktopSearchInputRef = useRef<HTMLInputElement>(null);
	const mobileSearchInputRef = useRef<HTMLInputElement>(null);
	const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
	const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
	const [companyContexts, setCompanyContexts] = useState<Company[]>([]);
	const [activeCompanyId, setActiveCompanyId] = useState<number | null>(user?.activeCompanyId ?? null);
	const [companyScopeMode, setCompanyScopeMode] = useState<'active' | 'all_companies'>(getStoredCompanyScopeMode());
	const isQuestionnaireRoute = location.pathname === '/app/seeker/create-plan';
	const narrowFromQuery = useMediaQuery('(max-width: 991.98px)');
	const isMobileLayout =
		isMobileLayoutProp !== undefined ? isMobileLayoutProp : narrowFromQuery;

	const personaLabel = useMemo(() => {
		if (!user) {
			return copy('Guest', 'Gast', 'Gost');
		}
		if (user.role === 'gardener') {
			return user.gardenerType === 'freelance'
				? copy('Freelance Gardener', 'Freiberuflicher Gärtner', 'Samostalni vrtlar')
				: copy('Gardener', 'Gärtner', 'Vrtlar');
		}
		if (user.role === 'seeker') {
			return copy('Garden Seeker', 'Gartensuchende', 'Vlasnik vrta');
		}
		return user.role.charAt(0).toUpperCase() + user.role.slice(1);
	}, [user, language]);

	const needsEmailVerification = !!user && !user.emailVerified;
	const activeCompanyName = useMemo(() => {
		if (companyScopeMode === 'all_companies') return 'All companies';
		if (activeCompanyId == null) return null;
		const aid = Number(activeCompanyId);
		const match = companyContexts.find(c => Number(c.id) === aid);
		return match?.name ?? user?.companyName ?? null;
	}, [activeCompanyId, companyContexts, user?.companyName, companyScopeMode]);

	useEffect(() => {
		setActiveCompanyId(user?.activeCompanyId ?? null);
	}, [user?.activeCompanyId]);
	useEffect(() => {
		setCompanyScopeMode(getStoredCompanyScopeMode());
	}, [user?.id]);


	useEffect(() => {
		if (!user?.id || !['company', 'gardener', 'admin'].includes(user.role)) {
			setCompanyContexts([]);
			return;
		}
		getMyCompanyContexts(user.id)
			.then((res) => {
				setCompanyContexts(Array.isArray(res.companies) ? res.companies : []);
				setActiveCompanyId(res.active_company_id ?? user.activeCompanyId ?? null);
			})
			.catch(() => setCompanyContexts([]));
	}, [user?.id, user?.role, user?.activeCompanyId, (user?.companyIds ?? []).join(',')]);

	const showCompanyWorkspaceMenu =
		!!user?.id && ['company', 'gardener', 'admin'].includes(user.role) && companyContexts.length > 0;
	const multiCompanyWorkspace = companyContexts.length > 1;

	// Perform search across all entities
	useEffect(() => {
		if (!searchQuery.trim() || !user) {
			setSearchResults([]);
			return;
		}

		const query = searchQuery.toLowerCase().trim();
		const results: SearchResult[] = [];

		// Search Plans
		if (['admin', 'company', 'gardener'].includes(user.role)) {
			// Gardeners see public plans
			const publicPlans = getPublicPlans();
			publicPlans
				.filter(plan => 
					plan.title.toLowerCase().includes(query) ||
					plan.city.toLowerCase().includes(query) ||
					plan.styleKey.toLowerCase().includes(query) ||
					plan.notes?.toLowerCase().includes(query)
				)
				.slice(0, 5)
				.forEach(plan => {
					results.push({
						type: 'plan',
						id: plan.id,
						title: plan.title,
						subtitle: `${plan.city} • ${plan.styleKey}`,
						url: user.role === 'gardener' ? `/app/seeker/plan/${plan.id}` : `/app/plans/${plan.id}`,
						icon: 'bi-journal-text'
					});
				});
		}

		if (user.role === 'seeker' || user.role === 'admin') {
			// Seekers see their own plans (for demo, show all plans since user IDs are random)
			// In production, this would filter by plan.seekerUserId === user.id
			const publicPlans = getPublicPlans();
			publicPlans
				.filter(plan =>
					plan.title.toLowerCase().includes(query) ||
					plan.city.toLowerCase().includes(query) ||
					plan.styleKey.toLowerCase().includes(query) ||
					plan.notes?.toLowerCase().includes(query)
				)
				.slice(0, 5)
				.forEach(plan => {
					results.push({
						type: 'plan',
						id: plan.id,
						title: plan.title,
						subtitle: `${plan.city} • ${plan.styleKey}`,
						url: `/app/seeker/plan/${plan.id}`,
						icon: 'bi-journal-text'
					});
				});
		}

		// Search Projects (for gardeners, companies, admins)
		if (['admin', 'company', 'gardener'].includes(user.role)) {
			(Array.isArray(projects) ? projects : [])
				.filter(project =>
					getProjectDisplayName(project).toLowerCase().includes(query) ||
					project.client.toLowerCase().includes(query) ||
					project.owner.toLowerCase().includes(query) ||
					(project.tags || []).some(tag => tag.toLowerCase().includes(query))
				)
				.slice(0, 5)
				.forEach(project => {
					results.push({
						type: 'project',
						id: project.id,
						title: getProjectDisplayName(project),
						subtitle: `${project.client} • ${project.status}`,
						url: '/app/projects',
						icon: 'bi-kanban'
					});
				});
		}

		// Search Tasks (for gardeners, companies, admins)
		if (['admin', 'company', 'gardener'].includes(user.role)) {
			tasks
				.filter(task =>
					task.title.toLowerCase().includes(query) ||
					task.plantName.toLowerCase().includes(query)
				)
				.slice(0, 5)
				.forEach(task => {
					results.push({
						type: 'task',
						id: task.id,
						title: task.title,
						subtitle: task.plantName,
						url: '/app/tasks',
						icon: 'bi-check2-circle'
					});
				});
		}

		// Search Plants (for gardeners, admins)
		if (['admin', 'gardener'].includes(user.role)) {
			plants
				.filter(plant =>
					plant.name.toLowerCase().includes(query) ||
					plant.species.toLowerCase().includes(query) ||
					plant.location.toLowerCase().includes(query)
				)
				.slice(0, 5)
				.forEach(plant => {
					results.push({
						type: 'plant',
						id: plant.id,
						title: plant.name,
						subtitle: `${plant.species} • ${plant.location}`,
						url: '/app/plants',
						icon: 'bi-flower3'
					});
				});
		}

		// Search Pages and Sections
		pageSearchIndex
			.filter(page => page.roles.includes(user.role))
			.filter(page => !page.adminCapability || hasAdminCapability(user, page.adminCapability))
			.filter(page => {
				const matchesTitle = page.title.toLowerCase().includes(query);
				const matchesDescription = page.description?.toLowerCase().includes(query);
				const matchesKeywords = page.keywords.some(keyword => keyword.toLowerCase().includes(query));
				return matchesTitle || matchesDescription || matchesKeywords;
			})
			.slice(0, 5)
			.forEach(page => {
				results.push({
					type: page.type,
					id: page.sectionId || page.url,
					title: page.title,
					subtitle: page.description,
					url: page.sectionId ? `${page.url}#${page.sectionId}` : page.url,
					icon: page.icon,
					sectionId: page.sectionId
				});
			});

		// Sort results: pages/sections first, then entities
		results.sort((a, b) => {
			if (a.type === 'page' || a.type === 'section') return -1;
			if (b.type === 'page' || b.type === 'section') return 1;
			return 0;
		});

		setSearchResults(results.slice(0, 15)); // Limit to 15 total results
	}, [searchQuery, user]);

	// Load profile picture - refresh when user changes or when navigating (to catch profile updates)
	useEffect(() => {
		if (!user?.id) {
			setProfilePictureUrl(null);
			return;
		}
		
		const loadProfilePicture = async () => {
			try {
				const profileData = await apiGet<{ profile: any }>(`/users/me/profile?userId=${user.id}`);
				const storedProfilePicture = normalizeStoredImageRef(profileData.profile?.profile_picture_url);
				if (storedProfilePicture) {
					setProfilePictureUrl(storedProfilePicture);
					return;
				}
				if (user.role === 'seeker') {
					const uid = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
					if (Number.isFinite(uid) && uid > 0) {
						const plans = await getPlans(uid).catch((): Plan[] => []);
						const sorted = [...plans].sort(
							(a, b) =>
								new Date(b.updated_at || b.created_at || 0).getTime() -
								new Date(a.updated_at || a.created_at || 0).getTime()
						);
						const row = sorted.find((p) => (p.before_image_url || '').trim().length > 0);
						const planPhoto = normalizeStoredImageRef(row?.before_image_url);
						if (planPhoto) {
							setProfilePictureUrl(planPhoto);
							return;
						}
					}
				}
				setProfilePictureUrl(null);
			} catch (error) {
				console.error('Failed to load profile picture:', error);
				setProfilePictureUrl(null);
			}
		};
		
		loadProfilePicture();
	}, [user?.id, location.pathname]); // Refresh when navigating (e.g., after saving profile)

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const t = event.target as Node;
			if (searchRef.current && !searchRef.current.contains(t)) {
				setShowResults(false);
			}
			if (mobileSearchRef.current && !mobileSearchRef.current.contains(t)) {
				setMobileSearchOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		setMobileSearchOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		const clearSearchFocus = () => {
			desktopSearchInputRef.current?.blur();
			mobileSearchInputRef.current?.blur();
			setShowResults(false);
		};
		window.addEventListener('app:clear-search-focus', clearSearchFocus);
		return () => window.removeEventListener('app:clear-search-focus', clearSearchFocus);
	}, []);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setShowResults(true);
	};

	const handleResultClick = (url: string, sectionId?: string) => {
		// If there's a sectionId, add it to the URL hash for highlighting
		const finalUrl = sectionId ? `${url.split('#')[0]}#${sectionId}` : url;
		navigate(finalUrl);
		setSearchQuery('');
		setShowResults(false);
		
		// Trigger highlighting after navigation (small delay to ensure page loads)
		if (sectionId) {
			setTimeout(() => {
				const element = document.querySelector(`[data-section-id="${sectionId}"]`);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'center' });
					// Add highlight class temporarily
					element.classList.add('search-highlight');
					setTimeout(() => {
						element.classList.remove('search-highlight');
					}, 2000);
				}
			}, 100);
		}
	};

	const handleSearchSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (searchResults.length > 0) {
			const firstResult = searchResults[0];
			handleResultClick(firstResult.url, firstResult.sectionId);
		}
	};

	const handleLogout = () => {
		logout();
		navigate('/', { replace: true });
	};

	const handleResultClickMobile = (url: string, sectionId?: string) => {
		handleResultClick(url, sectionId);
		setMobileSearchOpen(false);
	};

	const searchResultsDropdown = (
		<>
			{showResults && searchResults.length > 0 && (
				<div
					className="position-absolute top-100 start-0 end-0 mt-1 bg-white border rounded shadow-lg"
					style={{ zIndex: 1070, maxHeight: 'min(400px, 55vh)', overflowY: 'auto' }}
				>
					{searchResults.map((result) => (
						<button
							key={`${result.type}-${result.id}`}
							type="button"
							className="w-100 text-start btn btn-link text-decoration-none text-dark p-3 border-bottom transition-all"
							onClick={() => handleResultClickMobile(result.url, result.sectionId)}
							style={{ borderRadius: 0 }}
							title={result.tooltip}
						>
							<div className="d-flex align-items-center gap-2">
								<i className={`bi ${result.icon} text-muted transition-transform`}></i>
								<div className="flex-grow-1 min-w-0">
									<div className="fw-semibold text-truncate">{result.title}</div>
									{result.subtitle && (
										<div className="small text-muted text-truncate">{result.subtitle}</div>
									)}
								</div>
								<span
									className={`badge text-capitalize flex-shrink-0 ${
										result.type === 'page' || result.type === 'section'
											? 'bg-primary'
											: 'bg-secondary'
									}`}
								>
									{result.type}
								</span>
							</div>
						</button>
					))}
				</div>
			)}
			{showResults && searchQuery.trim() && searchResults.length === 0 && (
				<div
					className="position-absolute top-100 start-0 end-0 mt-1 bg-white border rounded shadow-lg p-3"
					style={{ zIndex: 1070 }}
				>
					<div className="text-muted text-center">No results found</div>
				</div>
			)}
		</>
	);

	const menuIconClass = isMobileLayout
		? isSidebarOpen
			? 'bi-x-lg'
			: 'bi-list'
		: isSidebarOpen
			? 'bi-chevron-left'
			: 'bi-chevron-right';

	const menuAria = isMobileLayout
		? isSidebarOpen
			? 'Close navigation menu'
			: 'Open navigation menu'
		: isSidebarOpen
			? 'Collapse sidebar'
			: 'Expand sidebar';

	return (
		<nav className="navbar navbar-expand bg-body-tertiary border-bottom sb-topbar">
			<div className="container-fluid px-2 px-sm-3">
				<div className="d-flex align-items-center gap-2 w-100 flex-wrap">
					<div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
						{onToggleSidebar && (
							<button
								type="button"
								className={`btn btn-outline-secondary ${isMobileLayout ? 'sb-menu-toggle' : ''}`}
								onClick={onToggleSidebar}
								aria-expanded={isSidebarOpen}
								aria-label={menuAria}
							>
								<i className={`bi ${menuIconClass}`}></i>
							</button>
						)}
						{!isQuestionnaireRoute && (
							<div className="d-none d-md-flex flex-grow-1 position-relative min-w-0" ref={searchRef}>
								<form className="w-100" role="search" onSubmit={handleSearchSubmit}>
									<div className="input-group">
										<span className="input-group-text bg-transparent border-end-0">
											<i className="bi bi-search"></i>
										</span>
										<input
											ref={desktopSearchInputRef}
											className="form-control border-start-0"
											type="search"
											placeholder={copy(
												'Search plans, projects, tasks, pages, settings…',
												'Pläne, Projekte, Aufgaben, Seiten, Einstellungen suchen…',
												'Pretraži planove, projekte, zadatke, stranice, postavke…'
											)}
											value={searchQuery}
											onChange={handleSearchChange}
											onFocus={() => setShowResults(true)}
										/>
									</div>
								</form>
								{searchResultsDropdown}
							</div>
						)}
						{!isQuestionnaireRoute && isMobileLayout && (
							<button
								type="button"
								className="btn btn-outline-secondary sb-menu-toggle"
								onClick={() => {
									setMobileSearchOpen((o) => !o);
									setShowResults(true);
								}}
								aria-expanded={mobileSearchOpen}
								aria-label={mobileSearchOpen ? copy('Close search', 'Suche schließen', 'Zatvori pretragu') : copy('Open search', 'Suche öffnen', 'Otvori pretragu')}
							>
								<i className="bi bi-search"></i>
							</button>
						)}
					</div>
					{user && (
						<div className="ms-auto d-flex align-items-center gap-2 gap-md-3 flex-shrink-0">
							<NotificationDropdown />
							<div className="dropdown">
							<button
								className="btn btn-light dropdown-toggle d-flex align-items-center gap-1 gap-md-2 px-2"
								style={{ minHeight: '2.75rem', maxWidth: isMobileLayout ? '10.5rem' : undefined }}
								data-bs-toggle="dropdown"
								aria-expanded="false"
							>
								{profilePictureUrl ? (
									<img
										src={getImageUrl(profilePictureUrl) || ''}
										alt={user.name || 'User'}
										className="rounded-circle flex-shrink-0"
										style={{ width: '32px', height: '32px', objectFit: 'cover' }}
										onError={() => setProfilePictureUrl(null)}
									/>
								) : null}
								<i
									className={`bi bi-person-circle flex-shrink-0 ${profilePictureUrl ? 'd-none' : ''}`}
									style={{ fontSize: '1.5rem' }}
								></i>
								{needsEmailVerification && (
									<span className="badge rounded-pill text-bg-danger" title="Email not verified">
										!
									</span>
								)}
								<span className="d-none d-sm-inline text-truncate small">{user.name}</span>
								{activeCompanyName && (
									<span
										className="badge rounded-pill d-none d-lg-inline flex-shrink-0 border border-success text-success bg-body-secondary"
										style={{ fontWeight: 500, maxWidth: '9rem' }}
										title="Active workspace"
									>
										<span className="text-truncate d-inline-block align-bottom" style={{ maxWidth: '7.5rem' }}>
											{activeCompanyName}
										</span>
									</span>
								)}
								<span className="badge rounded-pill text-bg-success text-capitalize d-none d-md-inline flex-shrink-0">
									{personaLabel}
								</span>
							</button>
							<ul className="dropdown-menu dropdown-menu-end shadow">
								{showCompanyWorkspaceMenu && (
									<>
										<li className="px-3 py-2">
											<div className="small fw-semibold text-muted mb-1">{copy('Workspace', 'Arbeitsbereich', 'Radni prostor')}</div>
											{multiCompanyWorkspace ? (
												<select
													className="form-select form-select-sm"
													aria-label={copy('Switch active company', 'Aktives Unternehmen wechseln', 'Promijeni aktivnu tvrtku')}
													value={companyScopeMode === 'all_companies' ? 'all' : String(activeCompanyId ?? '')}
													onChange={async (e) => {
														const value = e.target.value;
														if (value === 'all') {
															setStoredCompanyScopeMode('all_companies');
															setCompanyScopeMode('all_companies');
															window.location.reload();
															return;
														}
														const nextCompanyId = Number(value);
														if (!user?.id || !Number.isFinite(nextCompanyId) || nextCompanyId <= 0) return;
														try {
															await setMyActiveCompany(user.id, nextCompanyId);
															await refreshUser(user.id);
															const res = await getMyCompanyContexts(user.id);
															setCompanyContexts(Array.isArray(res.companies) ? res.companies : []);
															setActiveCompanyId(res.active_company_id ?? nextCompanyId);
															setStoredCompanyScopeMode('active');
															setCompanyScopeMode('active');
															notifyCompanyWorkspaceChanged();
														} catch (err) {
															console.error('Failed to switch active company', err);
															alert(
																err instanceof Error
																	? err.message
																	: 'Could not switch company. If this persists, run the active_company database migration.'
															);
														}
													}}
												>
													<option value="all">{copy('All companies (calendar & tasks)', 'Alle Unternehmen (Kalender & Aufgaben)', 'Sve tvrtke (kalendar i zadaci)')}</option>
													{companyContexts.map((company) => (
														<option key={company.id} value={company.id}>
															{company.name}
														</option>
													))}
												</select>
											) : (
												<div className="small text-muted">
													<span className="text-dark fw-medium">{companyContexts[0]?.name ?? copy('Company', 'Unternehmen', 'Tvrtka')}</span>
													<div className="mt-1">
														<Link className="small" to="/app/settings">
															{copy('Join another team in Settings', 'Weiterem Team in Einstellungen beitreten', 'Pridruži se drugom timu u postavkama')}
														</Link>
													</div>
												</div>
											)}
											<div className="small text-muted mt-2 mb-0">
												{multiCompanyWorkspace
													? copy(
														'Projects, team tools, and company settings follow the active company.',
														'Projekte, Team-Tools und Firmeneinstellungen folgen dem aktiven Unternehmen.',
														'Projekti, timski alati i postavke prate aktivnu tvrtku.'
													)
													: copy(
														'You are on one team. Add more from Settings → Company.',
														'Sie sind in einem Team. Weitere in Einstellungen → Unternehmen hinzufügen.',
														'Trenutno ste u jednom timu. Dodajte više u Postavke → Tvrtka.'
													)}
											</div>
										</li>
										<li>
											<hr className="dropdown-divider" />
										</li>
									</>
								)}
								<li>
									<Link className="dropdown-item" to="/app/profile">
										<i className={`bi ${needsEmailVerification ? 'bi-exclamation-triangle-fill text-danger' : 'bi-person'} me-2`}></i>
										{copy('Profile', 'Profil', 'Profil')}
									</Link>
								</li>
								<li>
									<Link className="dropdown-item" to="/app/settings">
										<i className="bi bi-gear me-2"></i>{copy('Settings', 'Einstellungen', 'Postavke')}
									</Link>
								</li>
								<li>
									<Link className="dropdown-item" to="/app/settings#subscription">
										<i className="bi bi-credit-card me-2"></i>{copy('Subscription', 'Abonnement', 'Pretplata')}
									</Link>
								</li>
								<li>
									<div className="px-3 py-2">
										<div className="small fw-semibold text-muted mb-1">{copy('Language', 'Sprache', 'Jezik')}</div>
										<select
											className="form-select form-select-sm"
											value={language}
											onChange={(e) => setLanguage(e.target.value as Language)}
										aria-label={copy('Language selector', 'Sprachauswahl', 'Odabir jezika')}
										>
											<option value="en">{t('language.english', 'English')}</option>
											<option value="de">{t('language.german', 'Deutsch')}</option>
											<option value="hr">{t('language.croatian', 'Hrvatski')}</option>
										</select>
									</div>
								</li>
								<li>
									<hr className="dropdown-divider" />
								</li>
								<li>
									<button className="dropdown-item text-danger" onClick={handleLogout} type="button">
										<i className="bi bi-box-arrow-right me-2"></i>{copy('Logout', 'Abmelden', 'Odjava')}
									</button>
								</li>
							</ul>
							</div>
						</div>
					)}
					{!isQuestionnaireRoute && isMobileLayout && mobileSearchOpen && (
						<div
							className="w-100 d-md-none pt-2 pb-1 border-top mt-1 order-last"
							ref={mobileSearchRef}
						>
							<div className="position-relative">
								<form className="w-100" role="search" onSubmit={handleSearchSubmit}>
									<div className="input-group">
										<span className="input-group-text bg-transparent border-end-0">
											<i className="bi bi-search"></i>
										</span>
										<input
											ref={mobileSearchInputRef}
											className="form-control border-start-0"
											type="search"
											placeholder={copy('Search plans, projects…', 'Pläne, Projekte suchen…', 'Pretraži planove, projekte…')}
											value={searchQuery}
											onChange={handleSearchChange}
											onFocus={() => setShowResults(true)}
										/>
									</div>
								</form>
								{searchResultsDropdown}
							</div>
						</div>
					)}
				</div>
			</div>
		</nav>
	);
}


